import type { ReactNode } from "react";
import { Link, NavLink } from "react-router";

import { HubLockup } from "@/components/Brand";
import { WalletButton } from "@/components/WalletButton";
import { Status } from "@/components/primitives";
import { runtimeConfig } from "@/lib/runtime-config";

const DESKTOP_NAV = [
  { to: "/", label: "Beranda", icon: "home", end: true },
  { to: "/dashboard", label: "Ringkasan", icon: "dashboard", end: false },
  { to: "/membership", label: "Membership", icon: "member", end: false },
  { to: "/vouchers", label: "E-Voucher", icon: "voucher", end: false },
  { to: "/catalog", label: "Katalog", icon: "catalog", end: false },
  { to: "/activity", label: "Aktivitas", icon: "activity", end: false },
  { to: "/protocol", label: "Protocol", icon: "protocol", end: false },
] as const;

const MOBILE_NAV = [
  { to: "/", label: "Beranda", icon: "home", end: true },
  { to: "/membership", label: "Member", icon: "member", end: false },
  { to: "/activity", label: "Aktivitas", icon: "activity", end: false },
  { to: "/dashboard", label: "Dompet", icon: "wallet", end: false },
] as const;

type NavIconName = (typeof DESKTOP_NAV)[number]["icon"] | "wallet";

export function AppShell({ children }: { children: ReactNode }) {
  const config = runtimeConfig();

  return (
    <div className="app-frame">
      <a href="#main" className="skip-link">
        Lompat ke konten
      </a>

      <aside className="desktop-wayfinding" aria-label="Navigasi utama">
        <Link to="/" className="brand-plaque" aria-label="ASETQUHub, ke beranda">
          <HubLockup size={32} />
        </Link>

        <div className="wayfinding-intro">
          <p className="wayfinding-intro__title">Property utility directory</p>
          <p>Gunakan ASQ untuk membership, voucher, dan kebutuhan properti.</p>
        </div>

        <nav className="wayfinding-nav">
          <ul>
            {DESKTOP_NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `wayfinding-link ${isActive ? "wayfinding-link--active" : ""}`
                  }
                >
                  <NavIcon name={item.icon} />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="wayfinding-status">
          <Status state="idle">{config.networkName}</Status>
          <p className="datum">Chain {config.chainId}</p>
          <p>Test environment · transaksi nyata, data utility contoh.</p>
        </div>
      </aside>

      <div className="app-stage">
        <header className="mobile-header">
          <Link to="/" aria-label="ASETQUHub, ke beranda">
            <HubLockup size={28} />
          </Link>
          <Status state="idle">{config.networkName}</Status>
        </header>

        <div className="mobile-wallet-bar">
          <WalletButton />
        </div>

        <header className="utility-bar">
          <div className="utility-bar__identity">
            <span aria-hidden="true" className="utility-bar__signal" />
            <div>
              <p>ASETQUHub</p>
              <span>Property utility network</span>
            </div>
          </div>
          <div className="utility-bar__actions">
            <Status state="idle">{config.networkName}</Status>
            <WalletButton />
          </div>
        </header>

        <main id="main" className="has-bottom-nav">
          {children}
        </main>

        <SiteFooter />
      </div>

      <nav className="mobile-nav" aria-label="Navigasi bawah">
        <ul>
          {MOBILE_NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `mobile-nav__link ${isActive ? "mobile-nav__link--active" : ""}`
                }
              >
                <NavIcon name={item.icon} />
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

function SiteFooter() {
  const config = runtimeConfig();

  return (
    <footer className="app-footer">
      <div className="sheet app-footer__inner">
        <div>
          <p className="app-footer__brand">ASETQUHub</p>
          <p>Utility test environment untuk membuktikan alur pembayaran ASQ secara end-to-end.</p>
        </div>
        <dl>
          <div>
            <dt>Registry</dt>
            <dd className="hexline">{config.utilityRegistry}</dd>
          </div>
          <div>
            <dt>ASQ Token</dt>
            <dd className="hexline">{config.asqToken}</dd>
          </div>
        </dl>
      </div>
    </footer>
  );
}

function NavIcon({ name }: { name: NavIconName }) {
  const paths: Record<NavIconName, ReactNode> = {
    home: <path d="M3 10.5 10 4l7 6.5V18H5v-7.5Z" />,
    dashboard: <path d="M3 4h6v6H3V4Zm8 0h6v3h-6V4Zm0 5h6v7h-6V9ZM3 12h6v4H3v-4Z" />,
    member: <path d="M10 3.5 12 7l4 .6-2.9 2.8.7 4-3.8-1.9-3.8 1.9.7-4L4 7.6 8 7l2-3.5Z" />,
    voucher: <path d="M3 5h14v3a2 2 0 0 0 0 4v3H3v-3a2 2 0 0 0 0-4V5Zm7 2v6" />,
    catalog: <path d="M4 4h5v5H4V4Zm7 0h5v5h-5V4ZM4 11h5v5H4v-5Zm7 0h5v5h-5v-5Z" />,
    activity: <path d="M3 11h3l1.8-4 3.2 7 2-4h4" />,
    protocol: <path d="M5 5h10v10H5V5Zm-2 5h14M10 3v14" />,
    wallet: <path d="M3 6.5h13v10H3v-10Zm1-3h9v3H4v-3Zm9 7h4" />,
  };

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}
